// src/services/apiService.ts

import { getErrorDetails, ErrorDetails } from '../utils/errorUtils';
import tokenManager from '../utils/tokenManager';

const DEFAULT_TIMEOUT = 60000; // 60 seconds
const LARGE_FILE_TIMEOUT = 120000; // 2 minutes for very large files
const LARGE_FILE_THRESHOLD = 500000; // Character count threshold (approx. 10,000 lines)

export interface ComparisonOptions {
    [key: string]: any;
}

export interface ComparisonResult {
    matchPercentage: number;
    diffCount: number;
    comparison?: any[];
    changeSummary?: string;
    chunkIndex?: number;
    startLine?: number;
    overallStats?: {
        matchPercentage: number;
        diffCount: number;
    };
    isSampled?: boolean;
    totalChunks?: number;
    allChunks?: ComparisonResult[];
    remainingChunksInfo?: {
        text1Chunks: string[];
        text2Chunks: string[];
        comparisonType: string | null;
        totalFetched: number;
        totalChunks: number;
    };
}

export interface ExtractionResult {
    text: string;
    metadata?: any;
}

export interface ExportResult {
    url?: string;
    filename?: string;
    [key: string]: any;
}

class ApiService {
    public baseURL: string;
    private timeout: number;

    constructor(baseURL: string, timeout = DEFAULT_TIMEOUT) {
        this.baseURL = baseURL;
        this.timeout = timeout;
    }

    async ensureSession(): Promise<string | null> {
        return await tokenManager.getToken();
    }

    private async fetchWithTimeout(url: string, options: RequestInit & { body?: string | FormData } = {}): Promise<Response> {
        // Determine if this is a large file request by checking body size
        let timeout = this.timeout;
        if (options.body && typeof options.body === 'string') {
            const bodySize = options.body.length;
            if (bodySize > LARGE_FILE_THRESHOLD) {
                timeout = LARGE_FILE_TIMEOUT;
            }
        }

        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), timeout);

        // Automatically include session token for internal tools
        if (url.includes('/api/tools/')) {
            const token = await tokenManager.getToken();
            if (token) {
                options.headers = {
                    ...options.headers,
                    'X-FileFusion-Session': token
                } as Record<string, string>;
            } else {
                console.warn(`API Service: No session token available for ${url}`);
            }
        }

        try {
            const response = await fetch(url, {
                ...options,
                signal: controller.signal
            });
            clearTimeout(timeoutId);
            return response;
        } catch (error) {
            clearTimeout(timeoutId);
            throw error;
        }
    }

    private async retryWithBackoff<T>(operation: () => Promise<T>, retries = 3): Promise<T> {
        for (let i = 0; i < retries; i++) {
            try {
                return await operation();
            } catch (error) {
                if (i === retries - 1) throw error;
                const delay = Math.min(1000 * Math.pow(2, i), 10000);
                await new Promise(resolve => setTimeout(resolve, delay));
            }
        }
        throw new Error('Retry limit reached');
    }

    // Add new method for chunk-based comparison of large files
    async compareTextsInChunks(
        text1: string,
        text2: string,
        comparisonType: string | null = null,
        options: ComparisonOptions | null = null
    ): Promise<ComparisonResult> {
        // If files are small enough, use regular comparison
        if (text1.length + text2.length < LARGE_FILE_THRESHOLD) {
            return this.compareTexts(text1, text2, comparisonType, options);
        }

        try {
            // If this is a paragraph comparison, handle it differently
            const isParagraph = comparisonType && comparisonType.startsWith('paragraph');

            if (isParagraph) {
                return await this.compareTexts(text1, text2, comparisonType, options);
            }

            const CHUNK_SIZE = 100000;
            const chunks1 = this.splitTextIntoChunks(text1, CHUNK_SIZE);
            const chunks2 = this.splitTextIntoChunks(text2, CHUNK_SIZE);

            const firstChunkResult = await this.compareTexts(
                chunks1[0],
                chunks2[0],
                comparisonType,
                options
            );

            if (chunks1.length === 1 && chunks2.length === 1) {
                return firstChunkResult;
            }

            const initialChunks: ComparisonResult[] = [];
            initialChunks.push({
                ...firstChunkResult,
                chunkIndex: 0,
                startLine: 0
            });

            const maxExtraChunks = Math.min(3, Math.min(chunks1.length, chunks2.length) - 1);

            for (let i = 0; i < maxExtraChunks; i++) {
                const chunkIndex = Math.floor((i + 1) * Math.min(chunks1.length, chunks2.length) / (maxExtraChunks + 1));

                const chunkResult = await this.compareTexts(
                    chunks1[Math.min(chunkIndex, chunks1.length - 1)],
                    chunks2[Math.min(chunkIndex, chunks2.length - 1)],
                    comparisonType,
                    options
                );

                chunkResult.chunkIndex = chunkIndex;
                initialChunks.push(chunkResult);
            }

            const averageMatchPercentage = initialChunks.reduce((sum, chunk) => sum + chunk.matchPercentage, 0) / initialChunks.length;

            const estimatedTotalDiffCount = Math.round(
                initialChunks.reduce((sum, chunk) => sum + chunk.diffCount, 0) / initialChunks.length * chunks1.length
            );

            const totalSampleSize = initialChunks.reduce((sum, chunk) => {
                const chunkSize = chunks1[chunk.chunkIndex!]?.length || 0;
                return sum + chunkSize;
            }, 0);

            const weightedMatchPercentage = initialChunks.reduce((sum, chunk) => {
                const chunkSize = chunks1[chunk.chunkIndex!]?.length || 0;
                return sum + (chunk.matchPercentage * chunkSize);
            }, 0) / (totalSampleSize || 1);

            const result: ComparisonResult = {
                ...firstChunkResult,
                overallStats: {
                    matchPercentage: Math.round((weightedMatchPercentage || averageMatchPercentage) * 100) / 100,
                    diffCount: estimatedTotalDiffCount
                },
                isSampled: true,
                totalChunks: chunks1.length,
                allChunks: initialChunks,
                remainingChunksInfo: {
                    text1Chunks: chunks1,
                    text2Chunks: chunks2,
                    comparisonType,
                    totalFetched: initialChunks.length,
                    totalChunks: chunks1.length
                }
            };

            return result;
        } catch (error: any) {
            throw getErrorDetails(error, error.response);
        }
    }

    private splitTextIntoChunks(text: string, chunkSize: number): string[] {
        const chunks: string[] = [];
        const lines = text.split('\n');
        let currentChunk = '';

        for (const line of lines) {
            if (currentChunk.length + line.length + 1 > chunkSize && currentChunk.length > 0) {
                chunks.push(currentChunk);
                currentChunk = '';
            }
            currentChunk += (currentChunk ? '\n' : '') + line;
        }

        if (currentChunk) {
            chunks.push(currentChunk);
        }

        return chunks;
    }

    async compareTexts(
        text1: string,
        text2: string,
        comparisonType: string | null = null,
        options: ComparisonOptions | null = null
    ): Promise<ComparisonResult> {
        try {
            const response = await this.retryWithBackoff(() =>
                this.fetchWithTimeout(`${this.baseURL}/api/tools/text-compare/compare`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        text1,
                        text2,
                        comparison_type: comparisonType,
                        options: options
                    })
                })
            );

            if (!response.ok) {
                if (response.status === 403) {
                    console.warn("API Service: Received 403. Clearing session token to force handshake retry.");
                    sessionStorage.removeItem('ff_security_token');
                    tokenManager.token = null;
                }

                const error = new Error('Comparison failed');
                const errorDetails = getErrorDetails(error, response);
                const enrichedError = new Error(errorDetails.message);
                Object.assign(enrichedError, errorDetails, { response });
                throw enrichedError;
            }

            return await response.json();
        } catch (error: any) {
            const errorDetails: ErrorDetails = getErrorDetails(error, error.response);
            const enrichedError = new Error(errorDetails.message || 'Request failed');
            Object.assign(enrichedError, errorDetails);
            throw enrichedError;
        }
    }

    async extractText(file: File): Promise<ExtractionResult> {
        try {
            const formData = new FormData();
            formData.append('file', file);

            const response = await this.fetchWithTimeout(`${this.baseURL}/api/tools/text-compare/extract-text`, {
                method: 'POST',
                body: formData
            });

            if (!response.ok) {
                throw new Error(`Extraction failed: ${response.statusText}`);
            }

            return await response.json();
        } catch (error: any) {
            throw getErrorDetails(error, error.response);
        }
    }

    async exportReport(
        comparisonResult: any,
        filename1: string,
        filename2: string,
        format = 'html',
        includeSummary = true
    ): Promise<ExportResult> {
        try {
            const response = await this.fetchWithTimeout(`${this.baseURL}/api/tools/text-compare/export-html`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    comparison_result: comparisonResult,
                    filename1,
                    filename2,
                    format,
                    includeSummary
                })
            });

            if (!response.ok) {
                throw new Error(`Export failed: ${response.statusText}`);
            }

            return await response.json();
        } catch (error: any) {
            throw getErrorDetails(error, error.response);
        }
    }

    async generateSummary(
        text1: string,
        text2: string,
        comparisonType: string | null = null,
        options: ComparisonOptions | null = null
    ): Promise<any> {
        try {
            const response = await this.retryWithBackoff(() =>
                this.fetchWithTimeout(`${this.baseURL}/api/tools/text-compare/summarize`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        text1,
                        text2,
                        comparison_type: comparisonType,
                        options: options
                    })
                })
            );

            if (!response.ok) {
                throw new Error('Summary generation failed');
            }

            return await response.json();
        } catch (error: any) {
            throw getErrorDetails(error, error.response);
        }
    }
}

const apiServiceInstance = new ApiService(
    import.meta.env.VITE_API_URL || 'http://localhost:8000'
);

export default apiServiceInstance;
